/**
 * Settings Modal
 * Zeigt detaillierte Cookie-Einstellungen mit Kategorien und Services
 */
import { pmLog, pmError } from './Logger.js';

export class SettingsModal {
    // Zweisprachige Fallback-Beschreibungen für bekannte Dienste (de/en)
    static SERVICE_DESCRIPTIONS = {
        google_analytics: { de: 'Webanalyse-Tool von Google',         en: 'Web analytics tool by Google' },
        facebook_pixel:   { de: 'Marketing und Conversion-Tracking',  en: 'Marketing and conversion tracking' },
        matomo:           { de: 'Selbst-gehostete Webanalyse',         en: 'Self-hosted web analytics' },
        youtube:          { de: 'Video-Plattform',                     en: 'Video platform' },
        vimeo:            { de: 'Video-Plattform',                     en: 'Video platform' },
        spotify:          { de: 'Musik- und Podcast-Streaming',        en: 'Music and podcast streaming' },
        google_maps:      { de: 'Interaktive Karten',                  en: 'Interactive maps' },
        soundcloud:       { de: 'Audio- und Musik-Streaming',          en: 'Audio and music streaming' },
    };

    // Ergänzende Anbieter-/Datenschutz-Infos und Default-Kategorie für Dienste,
    // die im Integrationsmanager aktiviert werden können, aber keine (oder keine
    // vollständigen) Metadaten vom Server mitbekommen (siehe SettingsLocalizer::
    // build_active_services_config()). Dienste mit eigener Kategorie vom Server
    // (z.B. automatisch erkannte Script-Dienste) nutzen `category` nur als Fallback.
    static EXTRA_SERVICE_INFO = {
        google_ads: { name: 'Google Ads', category: 'marketing', provider: 'Google LLC', privacy_url: 'https://policies.google.com/privacy' },
        google_tag_manager: { name: 'Google Tag Manager', category: 'marketing', provider: 'Google LLC', privacy_url: 'https://policies.google.com/privacy' },
        pinterest_pixel: { name: 'Pinterest Tag', category: 'marketing', provider: 'Pinterest Inc.', privacy_url: 'https://policy.pinterest.com/de/privacy-policy' },
        pinterest: { name: 'Pinterest', category: 'external', provider: 'Pinterest Inc.', privacy_url: 'https://policy.pinterest.com/de/privacy-policy' },
        twitter: { name: 'Twitter / X', category: 'external', provider: 'X Corp.', privacy_url: 'https://twitter.com/de/privacy' },
        instagram: { name: 'Instagram', category: 'external', provider: 'Meta Platforms Inc.', privacy_url: 'https://www.instagram.com/legal/privacy/' },
        threads: { name: 'Threads', category: 'external', provider: 'Meta Platforms Inc.', privacy_url: 'https://www.threads.net/privacy' },
        podigee: { name: 'Podigee', category: 'external', provider: 'Podigee GmbH', privacy_url: 'https://www.podigee.com/de/datenschutz' },
        deezer: { name: 'Deezer', category: 'external', provider: 'Deezer S.A.', privacy_url: 'https://www.deezer.com/legal/personal-datas' },
        acast: { name: 'Acast', category: 'external', provider: 'Acast AB', privacy_url: 'https://www.acast.com/privacy' },
        calendly: { name: 'Calendly', category: 'external', provider: 'Calendly LLC', privacy_url: 'https://calendly.com/privacy' },
        open_street_map: { name: 'OpenStreetMap', category: 'external', provider: 'OpenStreetMap Foundation', privacy_url: 'https://wiki.osmfoundation.org/wiki/Privacy_Policy' },
        wordpress_stats: { name: 'WordPress.com Statistiken', category: 'analytics', provider: 'Automattic Inc.', privacy_url: 'https://automattic.com/privacy/' },
        gravatar: { name: 'Gravatar', category: 'external', provider: 'Automattic Inc.', privacy_url: 'https://automattic.com/privacy/' },
        vg_wort: { name: 'VG WORT Zählmarken', category: 'necessary', provider: 'VG WORT', privacy_url: 'https://www.vgwort.de/datenschutz.html' },
        // Newsletter Dienste (Brevo/ActiveCampaign/Mailchimp/…): Titel, Anbieter
        // und Datenschutzlink hängen vom gewählten Anbieter ab und kommen daher
        // immer vollständig vom Server (siehe SettingsLocalizer::
        // build_active_services_config()/build_necessary_services_config()) -
        // kein statischer Fallback hier nötig.
        // Automatisch erkannte Script-Dienste: Kategorie kommt bereits vom Server,
        // hier nur Anbieter-/Datenschutz-Infos ergänzen.
        pixelhelper: { provider: 'Pixelhelper', privacy_url: 'https://pixelhelper.de/datenschutz/' },
        google_fonts: { provider: 'Google LLC', privacy_url: 'https://policies.google.com/privacy' },
        hotjar: { provider: 'Hotjar Ltd.', privacy_url: 'https://www.hotjar.com/legal/policies/privacy/' },
        intercom: { provider: 'Intercom Inc.', privacy_url: 'https://www.intercom.com/legal/privacy' },
        linkedin_insight: { provider: 'LinkedIn Ireland Unlimited Company', privacy_url: 'https://www.linkedin.com/legal/privacy-policy' },
        tiktok_pixel: { provider: 'TikTok Pte. Ltd.', privacy_url: 'https://www.tiktok.com/legal/privacy-policy' },
        crisp_chat: { provider: 'Crisp IM SAS', privacy_url: 'https://crisp.chat/en/privacy/' },
        hubspot: { provider: 'HubSpot Inc.', privacy_url: 'https://legal.hubspot.com/privacy-policy' },
        stripe: { provider: 'Stripe Inc.', privacy_url: 'https://stripe.com/privacy' },
        cloudflare_cdn: { provider: 'Cloudflare Inc.', privacy_url: 'https://www.cloudflare.com/privacypolicy/' },
        paypal: { provider: 'PayPal (Europe) S.à r.l. et Cie, S.C.A.', privacy_url: 'https://www.paypal.com/myaccount/privacy/privacyhub' },
        mollie: { provider: 'Mollie B.V.', privacy_url: 'https://www.mollie.com/gb/privacy' },
        woocommerce_payments: { provider: 'Automattic Inc. / Stripe, Inc.', privacy_url: 'https://automattic.com/privacy/' },
        apple_pay: { provider: 'Apple Distribution International Ltd.', privacy_url: 'https://www.apple.com/legal/privacy/' },
        google_pay: { provider: 'Google Ireland Limited', privacy_url: 'https://policies.google.com/privacy' },
        klarna: { provider: 'Klarna Bank AB (publ)', privacy_url: 'https://www.klarna.com/de/datenschutz/' },
        amazon_pay: { provider: 'Amazon Payments Europe s.c.a.', privacy_url: 'https://pay.amazon.de/help/201212490' },
        adyen: { provider: 'Adyen N.V.', privacy_url: 'https://www.adyen.com/policies-and-disclaimer/privacy-policy' },
        braintree: { provider: 'PayPal (Europe) S.à r.l. et Cie, S.C.A.', privacy_url: 'https://www.braintreepayments.com/de/legal/braintree-privacy-policy' },
        square: { provider: 'Block, Inc. (Square)', privacy_url: 'https://squareup.com/de/de/legal/general/privacy' },
        sumup: { provider: 'SumUp Limited', privacy_url: 'https://www.sumup.com/de-de/datenschutz/' },
        unzer: { provider: 'Unzer GmbH', privacy_url: 'https://www.unzer.com/de/datenschutz/' }
    };

    // Normalisiert eine beliebige Kategorie-/Consent-Bezeichnung (Admin-Dropdown,
    // Freitext-Consent-Typ oder cookie-database.json) auf eine der vier
    // tatsächlichen Consent-Gattungen, die das Banner kennt. Muss mit
    // DSGVO_Pixelmate_IntegrationSettings::normalize_consent_category() (PHP)
    // übereinstimmen.
    static CATEGORY_MAP = {
        necessary: 'necessary', notwendig: 'necessary', functional: 'necessary',
        funktional: 'necessary', essential: 'necessary',
        analytics: 'analytics', analyse: 'analytics', analytical: 'analytics',
        statistics: 'analytics', statistik: 'analytics', performance: 'analytics',
        marketing: 'marketing', werbung: 'marketing', advertising: 'marketing',
        advertisement: 'marketing', ads: 'marketing', targeting: 'marketing',
        external: 'external', extern: 'external', embedded: 'external',
        eingebettet: 'external', media: 'external', other: 'external',
        sonstiges: 'external', sonstige: 'external'
    };

    static normalizeCategory(raw, fallback = 'external') {
        const key = String(raw ?? '').trim().toLowerCase();
        if (key === '') return fallback;
        return SettingsModal.CATEGORY_MAP[key] || fallback;
    }

    // Alle Zahlungsanbieter-Vorlagen - muss mit
    // DSGVO_Pixelmate_IntegrationSettings::get_payment_service_keys() (PHP)
    // übereinstimmen. Dient hier nur dazu, für Anbieter OHNE eigene Cookie-
    // Einträge in der cookie-database.json (siehe serviceMapping in
    // getServiceCookieInfo()) einen ehrlichen, neutralen Hinweis statt eines
    // stillschweigend leeren Cookie-Blocks zu zeigen.
    static PAYMENT_SERVICE_IDS = new Set([
        'stripe', 'paypal', 'mollie', 'klarna', 'amazon_pay', 'adyen',
        'braintree', 'square', 'sumup', 'unzer', 'woocommerce_payments',
        'apple_pay', 'google_pay'
    ]);

    static MODAL_TEXTS = {
        tabSettings:         { de: 'Einstellungen',    en: 'Settings' },
        tabDataInfo:         { de: 'Datenauskunft',    en: 'Data Information' },
        settingsTitle:       { de: 'Cookie-Einstellungen', en: 'Cookie Settings' },
        settingsDescription: { de: 'Wir verwenden Cookies und ähnliche Technologien. Sie können unten für jeden Zweck einzeln zustimmen.', en: 'We use cookies and similar technologies. You can consent to each purpose individually below.' },
        saveSelection:       { de: 'Auswahl speichern', en: 'Save Selection' },
        acceptAll:           { de: 'Alle akzeptieren', en: 'Accept All' },
        rejectAll:           { de: 'Alle ablehnen',    en: 'Reject All' },
        required:            { de: 'Erforderlich',     en: 'Required' },
        privacy:             { de: 'Datenschutz',      en: 'Privacy' },
        dataInfoTitle:       { de: 'Datenauskunft',    en: 'Data Information' },
        dataInfoDesc:        { de: 'Hier sehen Sie, welche Daten zu Ihrer IP-Adresse gespeichert wurden.', en: 'Here you can see what data has been stored for your IP address.' },
        loading:             { de: 'Daten werden geladen…', en: 'Loading data…' },
        cookiesUsed:         { de: 'Verwendete Cookies:', en: 'Cookies used:' },
        paymentCookieFallback: {
            de: 'Für diesen Zahlungsanbieter liegen uns keine eigenen, geprüften Cookie-Namen vor. Der Anbieter kann bei der Zahlungsabwicklung eigene Cookies auf seiner eigenen Domain setzen – Details dazu findest du in der Datenschutzerklärung des Anbieters.',
            en: 'We don’t have verified cookie names of our own for this payment provider. The provider may set its own cookies on its own domain while processing the payment – see the provider’s privacy policy for details.'
        },
        purpose:             { de: 'Zweck:',           en: 'Purpose:' },
        yourIp:              { de: 'Ihr Pseudonym (gehashte IP-Adresse)', en: 'Your pseudonym (hashed IP address)' },
        allCookiesAccepted:  { de: 'Alle Cookies akzeptiert', en: 'All cookies accepted' },
        consentServices:     { de: 'Zugestimmte Dienste', en: 'Consented services' },
        noEntries:           { de: 'Keine gespeicherten Einträge für diese IP-Adresse.', en: 'No stored entries for this IP address.' },
        storedEntries:       { de: 'Gespeicherte Einträge', en: 'Stored entries' },
        errorLoading:        { de: 'Fehler beim Laden der Daten.', en: 'Error loading data.' },
        configUnavailable:   { de: 'Konfiguration nicht verfügbar.', en: 'Configuration not available.' },
        errorGeneric:        { de: 'Fehler:',          en: 'Error:' },
        categories: {
            necessary: {
                name:        { de: 'Notwendig',        en: 'Necessary' },
                description: { de: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich.', en: 'These cookies are required for the basic functions of the website.' }
            },
            analytics: {
                name:        { de: 'Analyse',          en: 'Analytics' },
                description: { de: 'Diese Cookies helfen uns, die Nutzung der Website zu verstehen.', en: 'These cookies help us understand how the website is used.' }
            },
            marketing: {
                name:        { de: 'Marketing',        en: 'Marketing' },
                description: { de: 'Diese Cookies werden für Werbung und Remarketing verwendet.', en: 'These cookies are used for advertising and remarketing.' }
            },
            external: {
                name:        { de: 'Externe Inhalte',  en: 'External Content' },
                description: { de: 'Diese Cookies erlauben das Laden externer Inhalte wie Videos und Karten.', en: 'These cookies allow loading external content such as videos and maps.' }
            }
        }
    };

    constructor(banner) {
        this.banner = banner;
        this.config = banner.config;
        this.design = banner.design;
        this.texts = banner.texts;

        this.modalElement = null;
        this.overlayElement = null;
        this.isOpen = false;
        // Element, das das Modal geöffnet hat - der Fokus kehrt beim Schließen dorthin zurück
        this.previouslyFocusedElement = null;
        this.keydownHandler = null;
        this.activeTab = 'settings'; // 'settings' oder 'cookies'
        this.cookieDatabase = null;

        // Sprache: gespeicherte Auswahl hat Vorrang, dann WP-Locale
        const wpLang = this.config.settings?.language || '';
        const savedLang = localStorage.getItem('pixelmate_lang');
        this.currentLang = savedLang || (wpLang.startsWith('de') ? 'de' : 'en');

        // Verfügbare Kategorien (strukturell; Texte werden über t() gerendert)
        this.categories = {
            necessary: { required: true,  services: [] },
            analytics: { required: false, services: [] },
            marketing: { required: false, services: [] },
            external:  { required: false, services: [] }
        };

        // Services aus Config laden
        this.loadServices();

        // Cookie-Datenbank laden – Promise speichern, damit open() darauf warten kann
        this.cookieDatabaseReady = this.loadCookieDatabase();
    }

    /**
     * Übersetzungshelfer: gibt den Text für den aktuellen currentLang zurück.
     * Unterstützt Punkt-Notation für verschachtelte Schlüssel (z.B. 'categories.necessary.name').
     */
    t(key) {
        const parts = key.split('.');
        let obj = SettingsModal.MODAL_TEXTS;
        for (const p of parts) obj = obj?.[p];
        return obj?.[this.currentLang] ?? obj?.de ?? key;
    }

    /**
     * Cookie-Datenbank laden
     */
    async loadCookieDatabase() {
        const url = pixelmate_vars.plugin_url + '/include/data/cookie-database.json';
        try {
            const response = await fetch(url);
            this.cookieDatabase = await response.json();
        } catch (error) {
            pmError('Failed to load cookie database:', error);
            this.cookieDatabase = {};
        }
    }

    /**
     * Beschreibungstext eines Cookies sprachabhängig auswählen
     */
    getCookieDescription(cookie) {
        return (this.currentLang === 'de' && cookie.description_de) ? cookie.description_de : cookie.description;
    }

    /**
     * Sprache umschalten und Modal neu aufbauen
     */
    switchLanguage(lang) {
        if (lang === this.currentLang) return;
        this.currentLang = lang;
        localStorage.setItem('pixelmate_lang', lang);
        this.rebuildModal();
    }

    /**
     * Modal-Inhalt neu rendern (erhält den Tab- und Consent-Zustand)
     */
    rebuildModal() {
        if (!this.modalElement) return;
        const activeTab = this.activeTab;
        this.modalElement.innerHTML = this.getModalHTML();
        this.attachModalListeners();
        this.loadCurrentConsent();
        if (activeTab !== 'settings') this.switchTab(activeTab);
    }

    /**
     * Lade aktive Services aus der Config
     */
    loadServices() {
        const services = this.config.activeServices || {};

        const desc = (id) => SettingsModal.SERVICE_DESCRIPTIONS[id]?.[this.currentLang]
            || SettingsModal.SERVICE_DESCRIPTIONS[id]?.de
            || '';

        // Google Analytics
        if (services.google_analytics) {
            this.categories.analytics.services.push({
                id: 'google_analytics',
                name: 'Google Analytics',
                description: services.google_analytics.description || desc('google_analytics'),
                provider: 'Google LLC',
                privacy_url: 'https://policies.google.com/privacy'
            });
        }

        // Facebook Pixel
        if (services.facebook_pixel) {
            this.categories.marketing.services.push({
                id: 'facebook_pixel',
                name: 'Facebook Pixel',
                description: services.facebook_pixel.description || desc('facebook_pixel'),
                provider: 'Meta Platforms Inc.',
                privacy_url: 'https://www.facebook.com/privacy/policy/'
            });
        }

        // Matomo
        if (services.matomo) {
            this.categories.analytics.services.push({
                id: 'matomo',
                name: 'Matomo',
                description: services.matomo.description || desc('matomo'),
                provider: 'Matomo',
                privacy_url: this.config.urls.privacy || '#'
            });
        }

        // YouTube
        if (services.youtube) {
            this.categories.external.services.push({
                id: 'youtube',
                name: 'YouTube',
                description: services.youtube.description || desc('youtube'),
                provider: 'Google LLC',
                privacy_url: 'https://policies.google.com/privacy'
            });
        }

        // Vimeo
        if (services.vimeo) {
            this.categories.external.services.push({
                id: 'vimeo',
                name: 'Vimeo',
                description: services.vimeo.description || desc('vimeo'),
                provider: 'Vimeo, Inc.',
                privacy_url: 'https://vimeo.com/privacy'
            });
        }

        // Spotify
        if (services.spotify) {
            this.categories.external.services.push({
                id: 'spotify',
                name: 'Spotify',
                description: services.spotify.description || desc('spotify'),
                provider: 'Spotify AB',
                privacy_url: 'https://www.spotify.com/privacy'
            });
        }

        // Google Maps
        if (services.google_maps) {
            this.categories.external.services.push({
                id: 'google_maps',
                name: 'Google Maps',
                description: services.google_maps.description || desc('google_maps'),
                provider: 'Google LLC',
                privacy_url: 'https://policies.google.com/privacy'
            });
        }

        // WordPress.com Statistiken
        if (services.wordpress_stats) {
            this.categories.analytics.services.push({
                id: 'wordpress_stats',
                name: 'WordPress.com Statistiken',
                description: services.wordpress_stats.description || desc('wordpress_stats'),
                provider: 'Automattic Inc.',
                privacy_url: 'https://automattic.com/privacy/'
            });
        }

        // Gravatar
        if (services.gravatar) {
            this.categories.external.services.push({
                id: 'gravatar',
                name: 'Gravatar',
                description: services.gravatar.description || desc('gravatar'),
                provider: 'Automattic Inc.',
                privacy_url: 'https://automattic.com/privacy/'
            });
        }

        // SoundCloud
        if (services.soundcloud) {
            this.categories.external.services.push({
                id: 'soundcloud',
                name: 'SoundCloud',
                description: services.soundcloud.description || desc('soundcloud'),
                provider: 'SoundCloud Global Limited & Co. KG',
                privacy_url: 'https://soundcloud.com/pages/privacy'
            });
        }

        // Notwendige Dienste (automatisch erkannt: WordPress-Kern, WooCommerce,
        // MemberPress, LearnDash, ... siehe SettingsLocalizer::build_necessary_services_config)
        const necessaryServices = services.necessary || {};
        Object.keys(necessaryServices).forEach(serviceId => {
            const svc = necessaryServices[serviceId];
            this.categories.necessary.services.push({
                id: serviceId,
                name: svc.title || serviceId,
                description: svc.description || '',
                provider: svc.provider || '',
                privacy_url: svc.privacy_url || '',
                cookies: svc.cookies || []
            });
        });

        // Alle übrigen im Integrationsmanager aktivierten Dienste automatisch
        // einsortieren (z.B. Google Tag Manager, Pinterest, Twitter/X, Instagram,
        // Threads, Podigee, Deezer, Acast, Calendly, OpenStreetMap sowie alle
        // automatisch erkannten Script-Dienste wie Hotjar, Stripe, HubSpot, ...).
        // Bereits oben einzeln behandelte Dienste werden dabei übersprungen.
        const alreadyHandled = new Set([
            'necessary', 'google_analytics', 'facebook_pixel', 'matomo',
            'youtube', 'vimeo', 'spotify', 'google_maps', 'soundcloud',
            'wordpress_stats', 'gravatar'
        ]);
        Object.keys(services).forEach(serviceId => {
            if (alreadyHandled.has(serviceId)) return;

            const svcConfig = services[serviceId];
            if (!svcConfig || typeof svcConfig !== 'object') return;

            const info = SettingsModal.EXTRA_SERVICE_INFO[serviceId] || {};
            const rawCategory = svcConfig.category || info.category;
            // Robuste Normalisierung auf eine der vier Consent-Kategorien, damit
            // KEIN aktivierter Dienst durchs Raster fällt (Admin-Vokabular wie
            // 'embedded'/'other'/'functional' oder cookie-database-Kategorien
            // werden zuverlässig zugeordnet, Unbekanntes landet unter 'external').
            const category = SettingsModal.normalizeCategory(rawCategory);

            if (!this.categories[category]) return;

            this.categories[category].services.push({
                id: serviceId,
                name: svcConfig.title || info.name || serviceId,
                description: svcConfig.description || info.description || '',
                provider: svcConfig.provider || info.provider || '',
                privacy_url: svcConfig.privacy_url || info.privacy_url || '',
                cookies: svcConfig.cookies || []
            });
        });
    }

    /**
     * Modal öffnen
     */
    async open() {
        if (this.isOpen) return;

        // Sicherstellen, dass die Cookie-DB geladen ist, bevor das Modal gerendert wird
        await this.cookieDatabaseReady;

        // Modal erstellen falls nicht vorhanden
        if (!this.modalElement) {
            this.createModal();
        }

        // Aktuelle Consent-Status laden
        this.loadCurrentConsent();

        // Element merken, das das Modal geöffnet hat, um den Fokus später
        // dorthin zurückzugeben (WCAG 2.4.3).
        this.previouslyFocusedElement = document.activeElement;

        // Anzeigen
        this.modalElement.style.display = 'flex';
        this.overlayElement.style.display = 'block';

        // Animation
        requestAnimationFrame(() => {
            this.modalElement.style.opacity = '1';
            this.modalElement.style.transform = 'translate(-50%, -50%) scale(1)';
            this.overlayElement.style.opacity = '1';
        });

        this.isOpen = true;

        // Body Scroll blockieren
        document.body.style.overflow = 'hidden';

        // Anders als der Banner ist das Modal tatsächlich modal (Seiten-Scroll
        // gesperrt, Overlay fängt Klicks ab). Deshalb gehört hier ein echter
        // Fokus-Fang dazu, sonst tabbt man aus einem scheinbar gesperrten Dialog
        // heraus in die dahinterliegende, nicht bedienbare Seite.
        this.modalElement.setAttribute('tabindex', '-1');
        try {
            this.modalElement.focus({ preventScroll: true });
        } catch (e) {
            this.modalElement.focus();
        }
    }

    /**
     * Liefert alle fokussierbaren Elemente im Modal in Dokumentreihenfolge.
     */
    getFocusableElements() {
        if (!this.modalElement) return [];

        const selector = [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
        ].join(', ');

        return Array.from(this.modalElement.querySelectorAll(selector))
            // offsetParent === null filtert ausgeblendete Elemente (z. B. der
            // gerade nicht aktive Tab) heraus. Die Schalter-Checkboxen haben
            // zwar 0x0 px, aber einen offsetParent — sie bleiben also drin.
            .filter(el => el.offsetParent !== null || el.type === 'checkbox');
    }

    /**
     * Tastaturbedienung des Modals: ESC schließt, Tab bleibt im Dialog gefangen.
     */
    handleModalKeydown(e) {
        if (!this.isOpen) return;

        if (e.key === 'Escape' || e.key === 'Esc') {
            e.preventDefault();
            // Schließen ohne Auswahl ändert die Einwilligung nicht — der bisherige
            // Stand bleibt unverändert bestehen.
            this.close();
            return;
        }

        if (e.key !== 'Tab') return;

        const focusable = this.getFocusableElements();
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (e.shiftKey && (active === first || active === this.modalElement)) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
        }
    }

    /**
     * Modal schließen
     */
    close() {
        if (!this.isOpen) return;

        // Animation raus
        this.modalElement.style.opacity = '0';
        this.modalElement.style.transform = 'translate(-50%, -50%) scale(0.95)';
        this.overlayElement.style.opacity = '0';

        setTimeout(() => {
            this.modalElement.style.display = 'none';
            this.overlayElement.style.display = 'none';
        }, 300);

        this.isOpen = false;

        // Body Scroll wieder erlauben
        document.body.style.overflow = '';

        // Fokus dorthin zurückgeben, wo er vor dem Öffnen war (Widerruf-Button,
        // Banner-Link, Shortcode-Button ...). Sonst landet er nach dem Schließen
        // wieder am Seitenanfang.
        const target = this.previouslyFocusedElement;
        this.previouslyFocusedElement = null;

        if (target && typeof target.focus === 'function' && target.isConnected) {
            try {
                target.focus({ preventScroll: true });
            } catch (e) {
                target.focus();
            }
        }
    }

    /**
     * Modal erstellen
     */
    createModal() {
        // Overlay
        this.overlayElement = document.createElement('div');
        this.overlayElement.className = 'pixelmate-modal-overlay';
        this.overlayElement.style.cssText = `
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            background: rgba(0, 0, 0, 0.7) !important;
            z-index: 1000000 !important;
            opacity: 0 !important;
            transition: opacity 0.3s ease !important;
            display: none !important;
        `;

        // Modal
        this.modalElement = document.createElement('div');
        // "settings-banner-wrapper" ist die Klasse der Legacy-Plugin-Version.
        // Sie bleibt als Alias erhalten, damit Custom-CSS, das Kunden vor der
        // Migration z.B. für theme-bedingte Button-Anpassungen eingefügt
        // haben, nach dem Update weiterhin greift.
        this.modalElement.className = 'pixelmate-settings-modal settings-banner-wrapper';
        this.modalElement.setAttribute('role', 'dialog');
        this.modalElement.setAttribute('aria-modal', 'true');
        this.modalElement.setAttribute('aria-labelledby', 'pixelmate-modal-title');
        this.modalElement.style.cssText = `
            position: fixed !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) scale(0.95) !important;
            width: 90% !important;
            max-width: 800px !important;
            max-height: 85vh !important;
            background: ${this.design.colors?.background || '#ffffff'} !important;
            color: ${this.design.colors?.text || '#333333'} !important;
            border-radius: ${this.design.border?.radius || '8px'} !important;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3) !important;
            z-index: 1000001 !important;
            opacity: 0 !important;
            transition: opacity 0.3s ease, transform 0.3s ease !important;
            display: none !important;
            overflow: hidden !important;
            flex-direction: column !important;
            font-family: inherit !important;
        `;

        this.modalElement.innerHTML = this.getModalHTML();

        // Zum DOM hinzufügen
        document.body.appendChild(this.overlayElement);
        document.body.appendChild(this.modalElement);

        // Sichtbare Fokus-Markierung (die Schalter im Modal sind versteckte
        // Checkboxen und ohne eigene Regel nicht als fokussiert erkennbar).
        this.banner.injectA11yStyles();

        // ESC und Tab-Fang gelten für das gesamte Dokument, solange das Modal offen
        // ist — der Fokus kann beim Öffnen noch außerhalb liegen.
        this.keydownHandler = (e) => this.handleModalKeydown(e);
        document.addEventListener('keydown', this.keydownHandler);

        // Tastaturbedienung der Aufklapp-Pfeile, delegiert am Modal-Container.
        //
        // Die Kopfzeilen selbst sind bewusst NICHT fokussierbar: Sie enthalten
        // teilweise einen Link zur Datenschutzerklärung des Anbieters, und ein Link
        // innerhalb eines Bedienelements ist weder gültiges ARIA noch sinnvoll
        // bedienbar. Fokussierbar ist deshalb der Pfeil - die Kopfzeile bleibt für
        // die Maus wie bisher anklickbar.
        //
        // Bewusst hier und nicht in attachModalListeners(): rebuildModal() ruft jene
        // Methode nach jedem Sprachwechsel erneut auf, während modalElement bestehen
        // bleibt - ein dort registrierter Listener würde sich stapeln und den
        // Aufklapp-Zustand pro Tastendruck mehrfach umschalten.
        this.modalElement.addEventListener('keydown', (e) => {
            if (e.key !== ' ' && e.key !== 'Spacebar' && e.key !== 'Enter') {
                return;
            }

            const serviceIcon = e.target.closest('.pixelmate-service-expand-icon');
            if (serviceIcon) {
                e.preventDefault();
                e.stopPropagation();
                this.toggleServiceDetails(serviceIcon.dataset.serviceId);
                return;
            }

            const categoryIcon = e.target.closest('.pixelmate-category-expand-icon');
            if (categoryIcon) {
                e.preventDefault();
                e.stopPropagation();
                this.toggleCategoryServices(categoryIcon.dataset.categoryId);
            }
        });

        // Event Listeners
        this.attachModalListeners();
    }

    /**
     * Modal HTML generieren
     */
    getModalHTML() {
        return `
            <!-- Schließen-Button absolut oben rechts -->
            <button class="pixelmate-modal-close" style="
                position: absolute !important;
                top: 14px !important;
                right: 16px !important;
                background: none !important;
                border: none !important;
                font-size: 26px !important;
                cursor: pointer !important;
                padding: 0 !important;
                width: 32px !important;
                height: 32px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                color: ${this.design.colors?.text || '#333333'} !important;
                opacity: 0.45 !important;
                transition: opacity 0.2s !important;
                z-index: 1 !important;
                line-height: 1 !important;
            " onmouseover="this.style.opacity='0.9'" onmouseout="this.style.opacity='0.45'">
                ×
            </button>

            <div class="pixelmate-modal-header" style="
                padding: 28px 24px 0 24px !important;
                border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
                flex-shrink: 0 !important;
            ">
                <div style="margin-bottom: 12px !important; padding-right: 32px !important;">
                    <h2 id="pixelmate-modal-title" style="margin: 0 !important; font-size: 20px !important; font-weight: 600 !important;">
                        ${this.texts.settingsTitle || this.t('settingsTitle')}
                    </h2>
                </div>

                <!-- Tab Navigation -->
                <div class="pixelmate-modal-tabs" style="
                    display: flex !important;
                    gap: 8px !important;
                    margin-bottom: -1px !important;
                ">
                    <button class="pixelmate-tab-btn" data-tab="settings" style="
                        background: none !important;
                        border: none !important;
                        border-bottom: 3px solid ${this.design.buttons?.accept?.background || '#0073aa'} !important;
                        box-sizing: border-box !important;
                        padding: 12px 20px !important;
                        cursor: pointer !important;
                        font-size: 14px !important;
                        font-weight: 600 !important;
                        line-height: 1em !important;
                        height: auto !important;
                        color: ${this.design.buttons?.accept?.background || '#0073aa'} !important;
                        transition: all 0.2s !important;
                    ">
                        ${this.t('tabSettings')}
                    </button>
                    <button class="pixelmate-tab-btn" data-tab="datenauskunft" style="
                        background: none !important;
                        border: none !important;
                        border-bottom: 3px solid transparent !important;
                        box-sizing: border-box !important;
                        padding: 12px 20px !important;
                        cursor: pointer !important;
                        font-size: 14px !important;
                        font-weight: 600 !important;
                        line-height: 1em !important;
                        height: auto !important;
                        color: #666666 !important;
                        transition: all 0.2s !important;
                    ">
                        ${this.t('tabDataInfo')}
                    </button>
                </div>
            </div>

            <div class="pixelmate-modal-body" style="
                padding: 16px 24px !important;
                max-height: 100vh !important;
                overflow-y: scroll !important;
                flex: 1 !important;
                min-height: 0 !important;
            ">
                <!-- Settings Tab Content -->
                <div class="pixelmate-tab-content" data-tab-content="settings" style="display: block !important;">
                    <p style="margin: 0 0 16px 0 !important; font-size: 14px !important; color: ${this.design.colors?.text || '#666666'} !important; opacity: 0.75 !important;">
                        ${this.texts.settingsDescription || this.t('settingsDescription')}
                    </p>

                    ${this.getCategoriesHTML()}
                </div>

                <!-- Datenauskunft Tab Content -->
                <div class="pixelmate-tab-content" data-tab-content="datenauskunft" style="display: none !important;">
                    <div class="pixelmate-datenauskunft-container">
                        ${this.getDatenauskunftHTML()}
                    </div>
                </div>
            </div>

            <div class="pixelmate-modal-footer" style="
                padding: 16px 24px !important;
                border-top: 1px solid rgba(0, 0, 0, 0.1) !important;
                display: flex !important;
                gap: 10px !important;
                justify-content: flex-end !important;
                background: rgba(0, 0, 0, 0.02) !important;
                flex-shrink: 0 !important;
            ">
                <button class="pixelmate-modal-save-selection" aria-label="${this.texts.saveSelection || this.t('saveSelection')}" style="
                    background: ${this.design.buttons?.accept?.background || '#0073aa'} !important;
                    color: ${this.design.buttons?.accept?.text || '#ffffff'} !important;
                    border: none !important;
                    box-sizing: border-box !important;
                    padding: 12px 24px !important;
                    border-radius: 4px !important;
                    font-size: 14px !important;
                    font-weight: 600 !important;
                    line-height: 1em !important;
                    height: auto !important;
                    cursor: pointer !important;
                    transition: opacity 0.2s !important;
                " ${this.getModalButtonHoverAttrs(this.design.buttons?.accept || {})}>
                    ${this.texts.saveSelection || this.t('saveSelection')}
                </button>

                <button class="pixelmate-modal-reject-all" aria-label="${this.texts.rejectButton || this.t('rejectAll')}" style="
                    background: ${this.design.buttons?.reject?.background || '#0073aa'} !important;
                    color: ${this.design.buttons?.reject?.text || '#ffffff'} !important;
                    border: none !important;
                    box-sizing: border-box !important;
                    padding: 12px 24px !important;
                    border-radius: 4px !important;
                    font-size: 14px !important;
                    font-weight: 600 !important;
                    line-height: 1em !important;
                    height: auto !important;
                    cursor: pointer !important;
                    transition: opacity 0.2s !important;
                " ${this.getModalButtonHoverAttrs(this.design.buttons?.reject || {})}>
                    ${this.texts.rejectButton || this.t('rejectAll')}
                </button>

                <button class="pixelmate-modal-accept-all" aria-label="${this.texts.acceptAll || this.t('acceptAll')}" style="
                    background: ${this.design.buttons?.accept?.background || '#0073aa'} !important;
                    color: ${this.design.buttons?.accept?.text || '#ffffff'} !important;
                    border: none !important;
                    box-sizing: border-box !important;
                    padding: 12px 24px !important;
                    border-radius: 4px !important;
                    font-size: 14px !important;
                    font-weight: 600 !important;
                    line-height: 1em !important;
                    height: auto !important;
                    cursor: pointer !important;
                    transition: opacity 0.2s !important;
                " ${this.getModalButtonHoverAttrs(this.design.buttons?.accept || {})}>
                    ${this.texts.acceptAll || this.t('acceptAll')}
                </button>
            </div>

            ${this.getFooterLinksHTML()}

            <!-- Minimaler Sprach-Footer -->
            <div class="pixelmate-modal-lang-footer" style="
                padding: 6px 24px !important;
                border-top: 1px solid rgba(0, 0, 0, 0.06) !important;
                display: flex !important;
                justify-content: center !important;
                align-items: center !important;
                gap: 10px !important;
                flex-shrink: 0 !important;
            ">
                ${['de','en'].map(l => `
                    <button class="pixelmate-lang-btn" data-lang="${l}" style="
                        background: none !important;
                        border: none !important;
                        padding: 2px 4px !important;
                        font-size: 11px !important;
                        font-weight: ${this.currentLang === l ? '700' : '400'} !important;
                        color: ${this.currentLang === l ? (this.design.colors?.text || '#333333') : 'rgba(0,0,0,0.3)'} !important;
                        cursor: pointer !important;
                        letter-spacing: 0.5px !important;
                        text-transform: uppercase !important;
                        transition: color 0.15s !important;
                        line-height: 1 !important;
                    " onmouseover="this.style.color='${this.design.colors?.text || '#333333'}'" onmouseout="this.style.color='${this.currentLang === l ? (this.design.colors?.text || '#333333') : 'rgba(0,0,0,0.3)'}'">
                        ${l}
                    </button>
                    ${l === 'de' ? `<span style="color: rgba(0,0,0,0.15) !important; font-size: 11px !important; line-height: 1 !important;">|</span>` : ''}
                `).join('')}
            </div>
        `;
    }

    /**
     * Erzeugt onmouseover/onmouseout Attribute für einen Footer-Button. Sind
     * eigene Hover-Farben konfiguriert, werden Hintergrund-/Textfarbe
     * getauscht, ansonsten dient eine reduzierte Deckkraft als Hover-Feedback.
     */
    getModalButtonHoverAttrs(buttonConfig) {
        const normalBg = buttonConfig.background || '#0073aa';
        const normalText = buttonConfig.text || '#ffffff';
        const hoverBg = buttonConfig.hoverBackground || '';
        const hoverText = buttonConfig.hoverText || '';

        if (!hoverBg && !hoverText) {
            return `onmouseover="this.style.opacity='0.9'" onmouseout="this.style.opacity='1'"`;
        }

        const overStyles = [
            `background-color: ${hoverBg || normalBg} !important`,
            `color: ${hoverText || normalText} !important`
        ].join('; ');
        const outStyles = [
            `background-color: ${normalBg} !important`,
            `color: ${normalText} !important`
        ].join('; ');

        return `onmouseover="this.style.cssText += '; ${overStyles}'" onmouseout="this.style.cssText += '; ${outStyles}'"`;
    }

    /**
     * Datenschutz-/Impressum-Links im Footer des Settings Modals,
     * analog zu den Links im Cookie Banner.
     */
    getFooterLinksHTML() {
        const privacyUrl = typeof this.banner.getPrivacyUrl === 'function' ? this.banner.getPrivacyUrl() : (this.banner.urls?.privacy || null);
        const imprintUrl = typeof this.banner.getImprintUrl === 'function' ? this.banner.getImprintUrl() : (this.banner.urls?.imprint || null);

        if (!privacyUrl && !imprintUrl) {
            return '';
        }

        const linkColor = this.design.colors?.link || '#0073aa';
        const links = [];

        if (privacyUrl) {
            links.push(`<a href="${privacyUrl}" target="_blank" rel="noopener" style="color: ${linkColor} !important; text-decoration: underline !important;">${this.texts.privacyLink || this.t('privacyLink') || 'Datenschutz'}</a>`);
        }

        if (imprintUrl) {
            links.push(`<a href="${imprintUrl}" target="_blank" rel="noopener" style="color: ${linkColor} !important; text-decoration: underline !important;">${this.texts.imprintLink || this.t('imprintLink') || 'Impressum'}</a>`);
        }

        return `
            <div class="pixelmate-modal-footer-links" style="
                padding: 8px 24px !important;
                border-top: 1px solid rgba(0, 0, 0, 0.06) !important;
                display: flex !important;
                justify-content: center !important;
                align-items: center !important;
                gap: 10px !important;
                flex-shrink: 0 !important;
                font-size: 12px !important;
            ">
                ${links.join('<span style="opacity: 0.5 !important;">|</span>')}
            </div>
        `;
    }

    /**
     * Kategorien HTML generieren (Material Design M3 List Style)
     */
    getCategoriesHTML() {
        let html = '<div style="border-top: 1px solid rgba(0, 0, 0, 0.08) !important;">';

        const textColor = this.design.colors?.text || '#333333';

        const showCategories = this.config.settings?.showCategories || {
            necessary: true, analytics: true, marketing: true, external: true
        };

        Object.keys(this.categories).forEach(categoryKey => {
            const category = this.categories[categoryKey];

            if (!showCategories[categoryKey]) {
                return;
            }

            if (categoryKey !== 'necessary' && category.services.length === 0) {
                return;
            }

            const hasServices = category.services.length > 0;

            html += `
                <div class="pixelmate-category" style="border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;">
                    <div style="
                        display: flex !important;
                        align-items: flex-start !important;
                        padding: 14px 0 !important;
                        gap: 12px !important;
                    ">
                        <div class="pixelmate-category-header" data-category-id="${categoryKey}" style="
                            flex: 1 !important;
                            min-width: 0 !important;
                            ${hasServices ? 'cursor: pointer !important;' : ''}
                        ">
                            <div style="display: flex !important; align-items: center !important; gap: 8px !important; margin-bottom: 3px !important;">
                                ${hasServices ? `
                                    <span class="pixelmate-category-expand-icon" data-category-id="${categoryKey}"
                                        role="button" tabindex="0" aria-expanded="false"
                                        aria-label="${this.t(`categories.${categoryKey}.name`)}"
                                        style="
                                        display: inline-block !important;
                                        width: 9px !important;
                                        height: 9px !important;
                                        border-right: 2px solid ${textColor} !important;
                                        border-bottom: 2px solid ${textColor} !important;
                                        opacity: 0.5 !important;
                                        transform: rotate(-45deg) !important;
                                        transition: transform 0.25s ease !important;
                                        flex-shrink: 0 !important;
                                        margin-top: 1px !important;
                                    "></span>
                                ` : `<span style="width: 9px !important; display: inline-block !important; flex-shrink: 0 !important;"></span>`}
                                <span style="
                                    font-size: 16px !important;
                                    font-weight: 600 !important;
                                    color: ${textColor} !important;
                                    line-height: 1.3 !important;
                                ">${this.t(`categories.${categoryKey}.name`)}</span>
                                ${category.required ? `
                                    <span style="
                                        font-size: 11px !important;
                                        color: ${textColor} !important;
                                        opacity: 0.5 !important;
                                        font-weight: 500 !important;
                                    ">(${this.t('required')})</span>
                                ` : ''}
                            </div>
                            <div style="
                                font-size: 13px !important;
                                color: ${textColor} !important;
                                opacity: 0.6 !important;
                                line-height: 1.4 !important;
                                padding-left: 17px !important;
                            ">${this.t(`categories.${categoryKey}.description`)}</div>
                        </div>

                        <label class="pixelmate-toggle" style="
                            position: relative !important;
                            display: inline-block !important;
                            width: 44px !important;
                            height: 24px !important;
                            flex-shrink: 0 !important;
                            margin-top: 3px !important;
                            ${category.required ? 'opacity: 0.5 !important; cursor: not-allowed !important;' : 'cursor: pointer !important;'}
                        ">
                            <input
                                type="checkbox"
                                data-category="${categoryKey}"
                                aria-label="${this.t(`categories.${categoryKey}.name`)}"
                                ${category.required ? 'checked disabled' : ''}
                                style="opacity: 0 !important; width: 0 !important; height: 0 !important;"
                            >
                            <span class="pixelmate-toggle-slider" style="
                                position: absolute !important;
                                cursor: ${category.required ? 'not-allowed' : 'pointer'} !important;
                                top: 0 !important;
                                left: 0 !important;
                                right: 0 !important;
                                bottom: 0 !important;
                                background-color: #ccc !important;
                                transition: 0.3s !important;
                                border-radius: 24px !important;
                            "></span>
                        </label>
                    </div>

                    <div class="pixelmate-category-services" data-category-id="${categoryKey}" style="
                        max-height: 0 !important;
                        overflow: hidden !important;
                        transition: max-height 0.32s ease !important;
                    ">
                        ${this.getServicesHTML(category.services, category.required)}
                    </div>
                </div>
            `;
        });

        html += '</div>';
        return html;
    }

    /**
     * Datenauskunft HTML generieren (initiales Ladeplatzhalter)
     */
    getDatenauskunftHTML() {
        return `
            <div style="margin-bottom: 20px !important;">
                <h3 style="margin: 0 0 12px 0 !important; font-size: 18px !important; font-weight: 600 !important;">
                    ${this.t('dataInfoTitle')}
                </h3>
                <p style="margin: 0 0 16px 0 !important; color: #666 !important; font-size: 14px !important;">
                    ${this.t('dataInfoDesc')}
                </p>
            </div>
            <div class="pixelmate-datenauskunft-results" style="min-height: 100px !important;">
                <p style="color: #999 !important; text-align: center !important; padding: 40px 0 !important;">
                    ${this.t('loading')}
                </p>
            </div>
        `;
    }

    /**
     * IP-Daten vom Server laden und im Datenauskunft-Tab anzeigen
     */
    async loadDatenauskunft() {
        const container = this.modalElement?.querySelector('.pixelmate-datenauskunft-results');
        if (!container) return;

        container.innerHTML = `<p style="color:#999 !important;text-align:center !important;padding:40px 0 !important;">${this.t('loading')}</p>`;

        try {
            if (!this.config?.ajax?.url) {
                container.innerHTML = `<p style="color:#c00 !important;padding:20px 0 !important;">${this.t('configUnavailable')}</p>`;
                return;
            }

            // Über den Banner senden: der holt bei abgelaufener Nonce (gecachte Seite)
            // automatisch eine frische nach und wiederholt die Anfrage einmal.
            const data = await this.banner.ajaxPost('pixelmate_get_ip_data');

            if (!data || !data.success) {
                container.innerHTML = `<p style="color:#c00 !important;padding:20px 0 !important;">${this.t('errorLoading')}</p>`;
                return;
            }

            const { ip_hash, entries } = data.data;
            const accentColor = this.design.buttons?.accept?.background || '#0073aa';

            let html = `
                <div style="margin-bottom:20px !important;padding:16px !important;background:rgba(0,0,0,0.03) !important;border-radius:6px !important;border:1px solid rgba(0,0,0,0.08) !important;">
                    <span style="font-size:13px !important;color:#666 !important;">${this.t('yourIp')}</span>
                    <div style="font-size:13px !important;font-weight:600 !important;color:${accentColor} !important;margin-top:4px !important;word-break:break-all !important;font-family:monospace !important;">${this.escapeHtml(ip_hash)}</div>
                </div>
            `;

            if (entries.length === 0) {
                html += `<p style="color:#999 !important;text-align:center !important;padding:20px 0 !important;">${this.t('noEntries')}</p>`;
            } else {
                html += `<h4 style="margin:0 0 12px 0 !important;font-size:15px !important;font-weight:600 !important;">${this.t('storedEntries')} (${entries.length})</h4>`;

                entries.forEach((entry, idx) => {
                    const consentSummary = this.formatConsentData(entry.consent_data);
                    html += `
                        <div style="margin-bottom:12px !important;padding:14px !important;border:1px solid rgba(0,0,0,0.08) !important;border-radius:6px !important;background:#fff !important;">
                            <div style="display:flex !important;justify-content:space-between !important;align-items:center !important;margin-bottom:8px !important;">
                                <span style="font-size:12px !important;color:#999 !important;">${this.escapeHtml(entry.timestamp)}</span>
                                <span style="font-size:11px !important;font-weight:600 !important;padding:2px 8px !important;border-radius:20px !important;background:${accentColor}20 !important;color:${accentColor} !important;">${this.escapeHtml(entry.consent_type)}</span>
                            </div>
                            <div style="font-size:13px !important;color:#444 !important;margin-bottom:6px !important;">${consentSummary}</div>
                            <div style="font-size:12px !important;color:#aaa !important;word-break:break-all !important;">${this.escapeHtml(entry.page_url)}</div>
                        </div>
                    `;
                });
            }

            container.innerHTML = html;
        } catch (err) {
            container.innerHTML = `<p style="color:#c00 !important;padding:20px 0 !important;">${this.t('errorGeneric')} ${this.escapeHtml(err.message)}</p>`;
        }
    }

    /**
     * Consent-Daten als Raw-String + Dienst-Chips formatieren
     */
    formatConsentData(consentDataRaw) {
        const rawStr = typeof consentDataRaw === 'string'
            ? consentDataRaw
            : JSON.stringify(consentDataRaw);

        const rawHtml = `<pre style="margin:0 0 10px 0 !important;font-size:11px !important;color:#555 !important;white-space:pre-wrap !important;word-break:break-all !important;background:rgba(0,0,0,0.03) !important;padding:8px !important;border-radius:4px !important;font-family:monospace !important;">${this.escapeHtml(rawStr)}</pre>`;

        let parsed = null;
        try { parsed = JSON.parse(rawStr); } catch (_) { /* not valid JSON */ }
        if (!parsed) return rawHtml;

        const accentColor = this.design.buttons?.accept?.background || '#0073aa';
        const chipStyle = `display:inline-block !important;font-size:11px !important;font-weight:600 !important;padding:2px 10px !important;border-radius:20px !important;margin:2px 4px 2px 0 !important;background:${accentColor}20 !important;color:${accentColor} !important;`;

        const categories    = parsed.categories || {};
        const services      = parsed.services   || {};
        const allCatKeys    = ['necessary', 'analytics', 'marketing', 'external'];
        const allAccepted   = allCatKeys.every(k => categories[k] === true);

        const categoryNames = {
            necessary: { de: 'Notwendig',       en: 'Necessary' },
            analytics: { de: 'Analyse',          en: 'Analytics' },
            marketing: { de: 'Marketing',        en: 'Marketing' },
            external:  { de: 'Externe Inhalte',  en: 'External Content' },
        };
        const lang = this.currentLang || 'de';

        let chips = '';
        if (allAccepted) {
            chips = `<span style="${chipStyle}">${this.t('allCookiesAccepted')}</span>`;
        } else {
            allCatKeys.forEach(key => {
                if (categories[key]) {
                    const label = categoryNames[key]?.[lang] ?? categoryNames[key]?.de ?? key;
                    chips += `<span style="${chipStyle}">${this.escapeHtml(label)}</span>`;
                }
            });
        }

        // Individuell freigegebene Dienste (z.B. YouTube via Content-Blocker)
        Object.entries(services).forEach(([svcId, val]) => {
            if (!val) return;
            const extra = SettingsModal.EXTRA_SERVICE_INFO[svcId];
            const label = extra?.name || svcId.replace(/_/g, ' ');
            chips += `<span style="${chipStyle}">${this.escapeHtml(label)}</span>`;
        });

        if (!chips) return rawHtml;

        return rawHtml + `<div style="margin-top:4px !important;"><span style="font-size:11px !important;color:#999 !important;margin-right:6px !important;">${this.t('consentServices')}:</span>${chips}</div>`;
    }

    /**
     * HTML-Zeichen escapen
     */
    escapeHtml(str) {
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    /**
     * Alle Cookies auf der aktuellen Seite scannen
     */
    scanCurrentCookies() {
        const cookies = document.cookie.split(';').map(cookie => {
            const [name, ...valueParts] = cookie.trim().split('=');
            return {
                name: name.trim(),
                value: valueParts.join('=')
            };
        }).filter(cookie => cookie.name);

        return cookies;
    }

    /**
     * Cookie in der Datenbank suchen
     */
    findCookieInDatabase(cookieName) {
        if (!this.cookieDatabase) return null;

        const results = [];

        // Durch alle Services iterieren
        Object.keys(this.cookieDatabase).forEach(serviceName => {
            const serviceCookies = this.cookieDatabase[serviceName];

            serviceCookies.forEach(cookieData => {
                // Exakte Übereinstimmung
                if (cookieData.cookie === cookieName) {
                    results.push({
                        service: serviceName,
                        ...cookieData
                    });
                }
                // Wildcard-Match (wenn wildcardMatch = 1)
                else if (cookieData.wildcardMatch === "1") {
                    // Wildcard-Matching implementieren
                    const pattern = cookieData.cookie.replace(/\*/g, '.*');
                    const regex = new RegExp(`^${pattern}$`, 'i');
                    if (regex.test(cookieName)) {
                        results.push({
                            service: serviceName,
                            ...cookieData,
                            matchType: 'wildcard'
                        });
                    }
                }
            });
        });

        return results.length > 0 ? results : null;
    }

    /**
     * Cookie-Scan-Ergebnisse anzeigen
     */
    displayCookieScanResults() {
        const resultsContainer = this.modalElement.querySelector('.pixelmate-cookie-scan-results');
        if (!resultsContainer) return;

        const cookies = this.scanCurrentCookies();

        if (cookies.length === 0) {
            resultsContainer.innerHTML = `
                <p style="color: #999 !important; text-align: center !important; padding: 40px 0 !important;">
                    Keine Cookies gefunden.
                </p>
            `;
            return;
        }

        let html = `
            <div style="margin-bottom: 16px !important;">
                <strong style="font-size: 16px !important;">
                    ${cookies.length} Cookie(s) gefunden
                </strong>
            </div>
        `;

        cookies.forEach(cookie => {
            const cookieInfo = this.findCookieInDatabase(cookie.name);

            if (cookieInfo && cookieInfo.length > 0) {
                // Cookie in Datenbank gefunden
                cookieInfo.forEach(info => {
                    html += this.renderCookieInfo(cookie.name, info);
                });
            } else {
                // Cookie nicht in Datenbank gefunden
                html += this.renderUnknownCookie(cookie.name);
            }
        });

        resultsContainer.innerHTML = html;
    }

    /**
     * Render Cookie-Info aus Datenbank
     */
    renderCookieInfo(cookieName, info) {
        const categoryColors = {
            'Functional': '#0073aa',
            'Analytics': '#ff9800',
            'Marketing': '#e91e63',
            'Necessary': '#4caf50'
        };

        const categoryColor = categoryColors[info.category] || '#666';

        return `
            <div style="
                margin-bottom: 16px !important;
                padding: 16px !important;
                border: 1px solid rgba(0, 0, 0, 0.1) !important;
                border-radius: 8px !important;
                background: white !important;
            ">
                <div style="display: flex !important; justify-content: space-between !important; align-items: start !important; margin-bottom: 12px !important;">
                    <div>
                        <h4 style="margin: 0 0 4px 0 !important; font-size: 16px !important; font-weight: 600 !important; color: #333 !important;">
                            ${cookieName}
                            ${info.matchType === 'wildcard' ? '<span style="font-size: 11px !important; color: #999 !important; font-weight: normal !important;">(Wildcard-Match)</span>' : ''}
                        </h4>
                        <div style="font-size: 13px !important; color: #666 !important;">
                            Service: <strong>${info.service}</strong>
                        </div>
                    </div>
                    <span style="
                        padding: 4px 12px !important;
                        background: ${categoryColor} !important;
                        color: white !important;
                        border-radius: 12px !important;
                        font-size: 11px !important;
                        font-weight: 600 !important;
                        text-transform: uppercase !important;
                    ">
                        ${info.category}
                    </span>
                </div>

                <div style="margin-bottom: 12px !important;">
                    <p style="margin: 0 !important; font-size: 14px !important; color: #666 !important; line-height: 1.5 !important;">
                        ${this.getCookieDescription(info)}
                    </p>
                </div>

                <div style="
                    display: grid !important;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)) !important;
                    gap: 12px !important;
                    padding: 12px !important;
                    background: rgba(0, 0, 0, 0.02) !important;
                    border-radius: 4px !important;
                    font-size: 13px !important;
                ">
                    ${info.domain ? `
                        <div>
                            <strong style="color: #333 !important;">Domain:</strong><br>
                            <span style="color: #666 !important;">${info.domain}</span>
                        </div>
                    ` : ''}
                    ${info.retentionPeriod ? `
                        <div>
                            <strong style="color: #333 !important;">Speicherdauer:</strong><br>
                            <span style="color: #666 !important;">${info.retentionPeriod}</span>
                        </div>
                    ` : ''}
                    ${info.dataController ? `
                        <div>
                            <strong style="color: #333 !important;">Anbieter:</strong><br>
                            <span style="color: #666 !important;">${info.dataController}</span>
                        </div>
                    ` : ''}
                </div>

                ${info.privacyLink ? `
                    <div style="margin-top: 12px !important;">
                        <a href="${info.privacyLink}" target="_blank" rel="noopener noreferrer" style="
                            color: ${this.design.colors?.link || '#0073aa'} !important;
                            font-size: 13px !important;
                            text-decoration: none !important;
                            font-weight: 600 !important;
                        " onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">
                            Datenschutzrichtlinie →
                        </a>
                    </div>
                ` : ''}
            </div>
        `;
    }

    /**
     * Render unbekanntes Cookie
     */
    renderUnknownCookie(cookieName) {
        return `
            <div style="
                margin-bottom: 16px !important;
                padding: 16px !important;
                border: 1px solid rgba(0, 0, 0, 0.1) !important;
                border-radius: 8px !important;
                background: rgba(255, 152, 0, 0.05) !important;
            ">
                <div style="display: flex !important; justify-content: space-between !important; align-items: start !important; margin-bottom: 8px !important;">
                    <h4 style="margin: 0 !important; font-size: 16px !important; font-weight: 600 !important; color: #333 !important;">
                        ${cookieName}
                    </h4>
                    <span style="
                        padding: 4px 12px !important;
                        background: #ff9800 !important;
                        color: white !important;
                        border-radius: 12px !important;
                        font-size: 11px !important;
                        font-weight: 600 !important;
                        text-transform: uppercase !important;
                    ">
                        Unbekannt
                    </span>
                </div>
                <p style="margin: 0 !important; font-size: 14px !important; color: #666 !important;">
                    Keine Informationen zu diesem Cookie in der Datenbank gefunden.
                </p>
            </div>
        `;
    }

    /**
     * Services HTML generieren (Material Design M3 List Style)
     */
    getServicesHTML(services, categoryRequired = false) {
        if (services.length === 0) return '';

        const textColor = this.design.colors?.text || '#333333';
        const linkColor = this.design.colors?.link || this.design.buttons?.accept?.background || '#0073aa';

        let html = '<div style="padding-bottom: 6px !important;">';

        services.forEach(service => {
            let cookieInfo = this.getServiceCookieInfo(service);
            let paymentFallback = false;

            // Zahlungsanbieter ohne eigene Cookie-Einträge (siehe
            // PAYMENT_SERVICE_IDS): statt den Aufklapp-Pfeil stillschweigend
            // wegzulassen (liest sich wie "keine Cookies vorhanden"), einen
            // neutralen Hinweis anzeigen, dass der Anbieter eigene Cookies
            // setzen kann - ohne Cookie-Namen zu erfinden.
            if ((!cookieInfo || cookieInfo.length === 0) && SettingsModal.PAYMENT_SERVICE_IDS.has(service.id)) {
                cookieInfo = true;
                paymentFallback = true;
            }

            const toggleHTML = categoryRequired ? `
                <span style="
                    flex-shrink: 0 !important;
                    padding: 2px 8px !important;
                    border-radius: 10px !important;
                    background: rgba(0, 0, 0, 0.06) !important;
                    font-size: 11px !important;
                    font-weight: 500 !important;
                    color: ${textColor} !important;
                    opacity: 0.6 !important;
                    white-space: nowrap !important;
                ">${this.t('required')}</span>
            ` : `
                <label class="pixelmate-service-toggle" style="
                    position: relative !important;
                    display: inline-block !important;
                    width: 44px !important;
                    height: 24px !important;
                    cursor: pointer !important;
                    flex-shrink: 0 !important;
                ">
                    <input
                        type="checkbox"
                        data-service="${service.id}"
                        aria-label="${service.name}"
                        style="opacity: 0 !important; width: 0 !important; height: 0 !important;"
                    >
                    <span class="pixelmate-service-toggle-slider" style="
                        position: absolute !important;
                        cursor: pointer !important;
                        top: 0 !important;
                        left: 0 !important;
                        right: 0 !important;
                        bottom: 0 !important;
                        background-color: #ccc !important;
                        transition: 0.3s !important;
                        border-radius: 24px !important;
                    "></span>
                </label>
            `;

            html += `
                <div class="pixelmate-service" data-service-id="${service.id}" style="
                    border-top: 1px solid rgba(0, 0, 0, 0.06) !important;
                    padding: 10px 0 10px 24px !important;
                ">
                    <div style="display: flex !important; align-items: center !important; gap: 10px !important;">
                        <div class="pixelmate-service-header" data-service-id="${service.id}" style="
                            flex: 1 !important;
                            min-width: 0 !important;
                            ${cookieInfo ? 'cursor: pointer !important;' : ''}
                        ">
                            <div style="display: flex !important; align-items: center !important; gap: 7px !important;">
                                ${cookieInfo ? `
                                    <span class="pixelmate-service-expand-icon" data-service-id="${service.id}"
                                        role="button" tabindex="0" aria-expanded="false"
                                        aria-label="${service.name}"
                                        style="
                                        display: inline-block !important;
                                        width: 7px !important;
                                        height: 7px !important;
                                        border-right: 1.5px solid ${textColor} !important;
                                        border-bottom: 1.5px solid ${textColor} !important;
                                        opacity: 0.45 !important;
                                        transform: rotate(-45deg) !important;
                                        transition: transform 0.22s ease !important;
                                        flex-shrink: 0 !important;
                                        margin-top: 1px !important;
                                    "></span>
                                ` : ''}
                                <span style="
                                    font-size: 14px !important;
                                    font-weight: 500 !important;
                                    color: ${textColor} !important;
                                    line-height: 1.3 !important;
                                ">${service.name}</span>
                            </div>
                            ${(service.description || service.provider || service.privacy_url) ? `
                                <div style="
                                    font-size: 12px !important;
                                    color: ${textColor} !important;
                                    opacity: 0.55 !important;
                                    margin-top: 2px !important;
                                    line-height: 1.4 !important;
                                    ${cookieInfo ? 'padding-left: 14px !important;' : ''}
                                ">
                                    ${service.description ? service.description : ''}
                                    ${service.description && (service.provider || service.privacy_url) ? ' · ' : ''}
                                    ${service.provider || ''}
                                    ${service.provider && service.privacy_url ? ' · ' : ''}
                                    ${service.privacy_url ? `<a href="${service.privacy_url}" target="_blank" rel="noopener noreferrer" style="color: ${linkColor} !important; text-decoration: none !important;" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">${this.t('privacy')}</a>` : ''}
                                </div>
                            ` : ''}
                        </div>
                        ${toggleHTML}
                    </div>

                    ${cookieInfo ? `
                        <div class="pixelmate-service-details" data-service-id="${service.id}" style="
                            max-height: 0 !important;
                            overflow: hidden !important;
                            transition: max-height 0.25s ease !important;
                            padding-left: 14px !important;
                        ">
                            ${paymentFallback ? this.renderPaymentCookieFallback() : this.renderServiceCookies(cookieInfo)}
                        </div>
                    ` : ''}
                </div>
            `;
        });

        html += '</div>';
        return html;
    }

    /**
     * Cookie-Informationen für einen Service abrufen
     * Akzeptiert entweder ein Service-Objekt (mit ggf. eingebetteten `cookies`,
     * z.B. für automatisch erkannte notwendige Dienste) oder eine Service-ID (String).
     */
    getServiceCookieInfo(service) {
        if (service && typeof service === 'object') {
            if (Array.isArray(service.cookies) && service.cookies.length > 0) {
                return service.cookies;
            }
            return this.getServiceCookieInfo(service.id);
        }

        const serviceId = service;
        if (!this.cookieDatabase) return null;

        // Service-Namen für die Datenbank zuordnen
        const serviceMapping = {
            'google_analytics': 'Google Analytics',
            'facebook_pixel': 'Facebook',  // Hinweis: Key in cookie-database.json ist "Facebook", nicht "Facebook Pixel"
            'matomo': 'Matomo',
            'youtube': 'Youtube',  // Hinweis: Key in cookie-database.json ist "Youtube" nicht "YouTube"
            'vimeo': 'Vimeo',
            'spotify': 'Spotify',
            'google_maps': 'Google Maps',
            'soundcloud': 'SoundCloud',
            'instagram': 'Instagram',
            'pinterest': 'Pinterest',
            'pinterest_pixel': 'Pinterest',
            'google_ads': 'Google Ads',
            'google_tag_manager': 'Google Tag Manager',
            'hotjar': 'Hotjar',
            'intercom': 'Intercom',
            'linkedin_insight': 'LinkedIn',
            'tiktok_pixel': 'TikTok',
            'hubspot': 'Hubspot',
            'stripe': 'Stripe',
            'cloudflare_cdn': 'Cloudflare',
            // Zahlungsanbieter: nur eintragen, wenn wirklich geprüfte Cookie-
            // Einträge in der cookie-database.json vorliegen. Anbieter ohne
            // Eintrag hier (Klarna, Amazon Pay, Adyen, Braintree, Square,
            // SumUp, Unzer, Apple/Google Pay) zeigen bewusst KEINE erfundenen
            // Cookie-Namen, sondern den neutralen Hinweis aus
            // getPaymentCookieFallback() - siehe getServicesHTML().
            'paypal': 'PayPal',
            'mollie': 'Mollie',
            // WooCommerce Payments läuft technisch über js.stripe.com - Cookies
            // sind Stripe-Cookies.
            'woocommerce_payments': 'Stripe'
        };

        const dbServiceName = serviceMapping[serviceId];
        if (!dbServiceName || !this.cookieDatabase[dbServiceName]) {
            return null;
        }

        return this.cookieDatabase[dbServiceName];
    }

    /**
     * Neutraler Hinweistext für Zahlungsanbieter, zu denen wir keine eigenen,
     * geprüften Cookie-Namen vorliegen haben (siehe PAYMENT_SERVICE_IDS).
     * Bewusst ohne erfundene Cookie-Namen/Laufzeiten - lieber ehrlich auf die
     * Datenschutzerklärung des Anbieters verweisen als falsche Angaben machen.
     */
    renderPaymentCookieFallback() {
        return `
            <div style="
                margin-top: 12px !important;
                padding-top: 12px !important;
                border-top: 1px solid ${this.design.border?.color || 'rgba(0, 0, 0, 0.1)'} !important;
            ">
                <p style="
                    margin: 0 !important;
                    font-size: 12px !important;
                    color: ${this.design.colors?.text || '#333333'} !important;
                    opacity: 0.75 !important;
                    line-height: 1.5 !important;
                ">
                    ${this.t('paymentCookieFallback')}
                </p>
            </div>
        `;
    }

    /**
     * Cookie-Details für einen Service rendern
     */
    renderServiceCookies(cookies) {
        if (!cookies || cookies.length === 0) return '';

        // Alle Cookies anzeigen
        const displayCookies = cookies;

        let html = `
            <div style="
                margin-top: 12px !important;
                padding-top: 12px !important;
                border-top: 1px solid ${this.design.border?.color || 'rgba(0, 0, 0, 0.1)'} !important;
            ">
                <div style="margin-bottom: 8px !important;">
                    <strong style="
                        font-size: 13px !important;
                        color: ${this.design.colors?.text || '#333333'} !important;
                        display: block !important;
                    ">
                        ${this.t('cookiesUsed')}
                    </strong>
                </div>
                <div style="display: flex !important; flex-direction: column !important; gap: 8px !important;">
        `;

        displayCookies.forEach(cookie => {
            const description = this.getCookieDescription(cookie) || '';
            const retentionPeriod = cookie.retentionPeriod || '';

            html += `
                <div style="
                    padding: 10px !important;
                    background: rgba(0, 0, 0, 0.02) !important;
                    border-radius: 4px !important;
                    font-size: 12px !important;
                ">
                    <div style="display: flex !important; justify-content: space-between !important; margin-bottom: 4px !important;">
                        <strong style="
                            color: ${this.design.colors?.text || '#333333'} !important;
                            font-family: monospace !important;
                            font-size: 12px !important;
                        ">
                            ${cookie.cookie}
                        </strong>
                        <span style="
                            font-size: 11px !important;
                            color: #999999 !important;
                            white-space: nowrap !important;
                            margin-left: 8px !important;
                        ">
                            ${retentionPeriod}
                        </span>
                    </div>
                    ${description ? `
                    <div style="
                        font-size: 12px !important;
                        color: #666666 !important;
                        line-height: 1.4 !important;
                    ">
                        <strong>${this.t('purpose')}</strong> ${description}
                    </div>
                    ` : ''}
                </div>
            `;
        });

        html += `
                </div>
            </div>
        `;

        return html;
    }

    /**
     * Event Listeners für Modal
     */
    attachModalListeners() {
        // Close Button
        const closeBtn = this.modalElement.querySelector('.pixelmate-modal-close');
        closeBtn?.addEventListener('click', () => this.close());

        // Overlay Click
        this.overlayElement.addEventListener('click', () => this.close());

        // Save Selection
        const saveBtn = this.modalElement.querySelector('.pixelmate-modal-save-selection');
        saveBtn?.addEventListener('click', () => this.saveSelection());

        // Accept All
        const acceptAllBtn = this.modalElement.querySelector('.pixelmate-modal-accept-all');
        acceptAllBtn?.addEventListener('click', () => this.acceptAll());

        const rejectAllBtn = this.modalElement.querySelector('.pixelmate-modal-reject-all');
        rejectAllBtn?.addEventListener('click', () => this.rejectAll());

        // Category Toggle Switches
        const categoryToggles = this.modalElement.querySelectorAll('input[data-category]');
        categoryToggles.forEach(toggle => {
            toggle.addEventListener('change', (e) => {
                this.updateToggleUI(e.target);
                this.handleCategoryToggle(e.target);
            });
        });

        // Service Toggle Switches
        const serviceToggles = this.modalElement.querySelectorAll('input[data-service]');
        serviceToggles.forEach(toggle => {
            toggle.addEventListener('change', (e) => {
                this.updateServiceToggleUI(e.target);
            });
        });

        // Language Switcher
        const langBtns = this.modalElement.querySelectorAll('.pixelmate-lang-btn');
        langBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                this.switchLanguage(e.target.dataset.lang);
            });
        });

        // Tab Switches
        const tabBtns = this.modalElement.querySelectorAll('.pixelmate-tab-btn');
        tabBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const tabName = e.target.dataset.tab;
                this.switchTab(tabName);
            });
        });

        // Service Headers (click to expand cookie details)
        const serviceHeaders = this.modalElement.querySelectorAll('.pixelmate-service-header');
        serviceHeaders.forEach(header => {
            header.addEventListener('click', (e) => {
                e.preventDefault();
                const serviceId = header.dataset.serviceId;
                this.toggleServiceDetails(serviceId);
            });
        });

        // Category Headers (click to expand service list)
        const categoryHeaders = this.modalElement.querySelectorAll('.pixelmate-category-header');
        categoryHeaders.forEach(header => {
            header.addEventListener('click', (e) => {
                e.preventDefault();
                const categoryId = header.dataset.categoryId;
                this.toggleCategoryServices(categoryId);
            });
        });

    }

    /**
     * Dienste-Liste einer Kategorie ein-/ausklappen (animiert via max-height)
     */
    toggleCategoryServices(categoryId) {
        const servicesElement = this.modalElement.querySelector(`.pixelmate-category-services[data-category-id="${categoryId}"]`);
        if (!servicesElement) return;

        const icon = this.modalElement.querySelector(`.pixelmate-category-expand-icon[data-category-id="${categoryId}"]`);
        const isExpanded = !!(servicesElement.style.maxHeight && servicesElement.style.maxHeight !== '0px');

        if (isExpanded) {
            servicesElement.style.maxHeight = '0';
            if (icon) icon.style.transform = 'rotate(-45deg)';
        } else {
            servicesElement.style.maxHeight = '2000px';
            if (icon) icon.style.transform = 'rotate(45deg)';
        }

        if (icon) icon.setAttribute('aria-expanded', String(!isExpanded));
    }

    /**
     * Service-Details ein-/ausklappen (animiert via max-height)
     */
    toggleServiceDetails(serviceId) {
        const detailsElement = this.modalElement.querySelector(`.pixelmate-service-details[data-service-id="${serviceId}"]`);
        if (!detailsElement) return;

        const icon = this.modalElement.querySelector(`.pixelmate-service-expand-icon[data-service-id="${serviceId}"]`);
        const isExpanded = !!(detailsElement.style.maxHeight && detailsElement.style.maxHeight !== '0px');

        if (isExpanded) {
            detailsElement.style.maxHeight = '0';
            if (icon) icon.style.transform = 'rotate(-45deg)';
        } else {
            detailsElement.style.maxHeight = '600px';
            if (icon) icon.style.transform = 'rotate(45deg)';
        }

        if (icon) icon.setAttribute('aria-expanded', String(!isExpanded));
    }

    /**
     * Tab wechseln
     */
    switchTab(tabName) {
        this.activeTab = tabName;

        // Tab-Buttons aktualisieren
        const tabBtns = this.modalElement.querySelectorAll('.pixelmate-tab-btn');
        tabBtns.forEach(btn => {
            const isActive = btn.dataset.tab === tabName;
            const activeColor = this.design.buttons?.accept?.background || '#0073aa';

            if (isActive) {
                btn.style.borderBottomColor = activeColor;
                btn.style.color = activeColor;
            } else {
                btn.style.borderBottomColor = 'transparent';
                btn.style.color = '#666666';
            }
        });

        // Tab-Content aktualisieren
        const tabContents = this.modalElement.querySelectorAll('.pixelmate-tab-content');
        tabContents.forEach(content => {
            const isActive = content.dataset.tabContent === tabName;
            content.style.display = isActive ? 'block' : 'none';
        });

        // Wenn Datenauskunft-Tab aktiviert wird, Daten laden
        if (tabName === 'datenauskunft') {
            this.loadDatenauskunft();
        }
    }

    /**
     * Category Toggle UI aktualisieren
     */
    updateToggleUI(checkbox) {
        const slider = checkbox.nextElementSibling;
        if (checkbox.checked) {
            slider.style.backgroundColor = this.design.buttons?.accept?.background || '#0073aa';
        } else {
            slider.style.backgroundColor = '#ccc';
        }

        // Slider-Knopf Position
        const knob = document.createElement('span');
        knob.style.cssText = `
            position: absolute !important;
            content: "" !important;
            height: 20px !important;
            width: 20px !important;
            left: ${checkbox.checked ? '21px' : '2px'} !important;
            bottom: 2px !important;
            background-color: white !important;
            transition: 0.3s !important;
            border-radius: 50% !important;
        `;

        // Alten Knopf entfernen, neuen hinzufügen
        const oldKnob = slider.querySelector('span');
        if (oldKnob) oldKnob.remove();
        slider.appendChild(knob);
    }

    /**
     * Service Toggle UI aktualisieren
     */
    updateServiceToggleUI(checkbox) {
        const slider = checkbox.nextElementSibling;
        if (checkbox.checked) {
            slider.style.backgroundColor = this.design.buttons?.accept?.background || '#0073aa';
        } else {
            slider.style.backgroundColor = '#ccc';
        }

        // Slider-Knopf Position
        const knob = document.createElement('span');
        knob.style.cssText = `
            position: absolute !important;
            content: "" !important;
            height: 20px !important;
            width: 20px !important;
            left: ${checkbox.checked ? '21px' : '2px'} !important;
            bottom: 2px !important;
            background-color: white !important;
            transition: 0.3s !important;
            border-radius: 50% !important;
        `;

        // Alten Knopf entfernen, neuen hinzufügen
        const oldKnob = slider.querySelector('span');
        if (oldKnob) oldKnob.remove();
        slider.appendChild(knob);
    }

    /**
     * Category Toggle Handler - synchronisiert Services mit Category
     */
    handleCategoryToggle(checkbox) {
        const category = checkbox.dataset.category;
        const isChecked = checkbox.checked;

        // Finde alle Service-Toggles in dieser Kategorie und aktualisiere sie
        const categoryElement = checkbox.closest('.pixelmate-category');
        if (categoryElement) {
            const serviceToggles = categoryElement.querySelectorAll('input[data-service]');
            serviceToggles.forEach(serviceToggle => {
                serviceToggle.checked = isChecked;
                this.updateServiceToggleUI(serviceToggle);
            });
        }
    }

    /**
     * Aktuellen Consent laden und Toggles setzen
     */
    loadCurrentConsent() {
        const consent = this.banner.scriptBlocker.consent;
        const serviceConsent = this.banner.scriptBlocker.serviceConsent;

        // Category Toggles setzen
        Object.keys(consent).forEach(category => {
            const checkbox = this.modalElement.querySelector(`input[data-category="${category}"]`);
            if (checkbox && !checkbox.disabled) {
                checkbox.checked = consent[category];
                this.updateToggleUI(checkbox);
            }
        });

        // Service Toggles setzen
        const serviceToggles = this.modalElement.querySelectorAll('input[data-service]');
        serviceToggles.forEach(toggle => {
            const serviceId = toggle.dataset.service;
            // Service-Status aus serviceConsent laden, oder fallback auf Category-Consent
            const isChecked = serviceConsent[serviceId] !== undefined
                ? serviceConsent[serviceId]
                : this.getServiceCategoryConsent(serviceId);
            toggle.checked = isChecked;
            this.updateServiceToggleUI(toggle);
        });
    }

    /**
     * Ermittelt den Consent-Status der Kategorie eines Services
     */
    getServiceCategoryConsent(serviceId) {
        const consent = this.banner.scriptBlocker.consent;

        // Kategorie anhand der bereits eingeordneten Services ermitteln (siehe
        // loadServices()), statt einer separat zu pflegenden Zuordnungsliste.
        const category = Object.keys(this.categories).find(categoryKey =>
            this.categories[categoryKey].services.some(service => service.id === serviceId)
        );
        return category ? consent[category] : false;
    }

    /**
     * Auswahl speichern
     */
    saveSelection() {
        const categories = {};

        // Alle Category-Toggles auslesen
        const categoryToggles = this.modalElement.querySelectorAll('input[data-category]');
        categoryToggles.forEach(toggle => {
            categories[toggle.dataset.category] = toggle.checked;
        });

        // Service-spezifische Toggles auslesen
        const serviceToggles = this.modalElement.querySelectorAll('input[data-service]');
        const serviceStates = {};

        serviceToggles.forEach(toggle => {
            const serviceId = toggle.dataset.service;
            const isChecked = toggle.checked;
            serviceStates[serviceId] = isChecked;
        });

        pmLog('Saving selection:', categories, 'Services:', serviceStates);

        // Prüfen ob CSP aktiviert ist und mindestens eine Kategorie oder Service aktiviert wurde
        const csp = this.banner.csp || {};
        const hasAnyCategoryEnabled = Object.values(categories).some(value => value === true);
        const hasAnyServiceEnabled = Object.values(serviceStates).some(value => value === true);
        const hasAnyConsentEnabled = hasAnyCategoryEnabled || hasAnyServiceEnabled;

        if (csp.reloadOnConsentChange && hasAnyConsentEnabled) {
            // ScriptBlocker überspringen - der Reload übernimmt die Script-Aktivierung sauber
            this.banner.setConsent('custom', { categories, services: serviceStates });
            this.banner.trackingDataPreserver.saveTrackingDataBeforeReload();
            window.location.reload();
        } else {
            // Consent aktualisieren und Scripts aktivieren
            const updatedConsent = this.banner.scriptBlocker.updateConsent(categories, serviceStates);
            this.banner.setConsent('custom', updatedConsent);

            // Geblockte Inhalte freischalten, für die jetzt Consent erteilt wurde
            Object.keys(serviceStates).forEach(service => {
                if (serviceStates[service]) {
                    this.banner.contentBlocker.unblockServiceContent(service);
                }
            });

            // Modal schließen
            this.close();

            // Banner verstecken wenn noch sichtbar
            if (this.banner.isVisible) {
                this.banner.hideBanner();
            }
        }
    }

    /**
     * Alle akzeptieren
     */
    acceptAll() {
        // Modal schließen bevor banner.acceptAll() aufgerufen wird (für den Fall ohne Seiten-Reload)
        this.close();
        // Banner-Methode delegieren: setzt Consent-Typ korrekt auf 'accept' statt 'custom',
        // damit die CSP beim nächsten Seitenaufruf deaktiviert wird.
        this.banner.acceptAll();
    }

    /**
     * Alle nicht-notwendigen Cookies ablehnen
     */
    rejectAll() {
        // Modal schließen bevor banner.rejectAll() aufgerufen wird (analog zu acceptAll())
        this.close();
        this.banner.rejectAll();
    }
}
